import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getSetupGuide } from './api';
import { useCurrentWorkspace } from '@/context/current-workspace';

/** Matches what `useSetupGuideExperiment` used to set for itself, now that it shares this query. */
const QUERY_STALE_TIME = 5000;

type UseSetupGuideOptions = {
  /**
   * Suppress the fetch. Folded in rather than left to each caller so the `['setupGuide', id]`
   * entry has one owner — see the note on this module.
   */
  enabled?: boolean;
  /**
   * Keep the previous workspace's guide on screen while the next one loads. Opt-in, and only the
   * experiment hook wants it: home stays mounted across a workspace switch and its `workspace-setup`
   * and `default` screens are not keyed by id (`home-screen-view.tsx`), so a placeholder there
   * reads as settled data for the wrong workspace — `selectHomeScreen` would pick a screen from it
   * and the reconcile would write it back under the new id.
   */
  keepPreviousWorkspaceData?: boolean;
};

/**
 * The only fetcher of the current workspace's setup guide.
 *
 * It used to have a twin. `useSetupGuideExperiment` ran its own `useQuery` on the same
 * `['setupGuide', id]` key with a `queryFn` that swallowed errors into `null`, while this one let
 * them reject. TanStack gives a cache entry one `queryFn`, so whichever observer mounted first
 * decided what a failed fetch meant for every reader — `isError` here, `isSuccess` with `data:
 * null` there. `selectHomeScreen` branches on that, so the home screen's behaviour on an API
 * failure depended on mount order. The two also disagreed about the workspace id: this hook read
 * `getWorkspaceIdFromCurrentUrl()`, a digits-only parse that returns null on `/workspaces/last/*`
 * and disabled the query outright, while the twin used the resolved `useCurrentWorkspace()`.
 *
 * Both are settled here. One `queryFn`, errors reject and reach callers as `isError`, and the id
 * is the resolved one so the `last` alias fetches like any other workspace. FPS-4144.
 */
export const useSetupGuide = ({ enabled = true, keepPreviousWorkspaceData = false }: UseSetupGuideOptions = {}) => {
  const workspaceId = useCurrentWorkspace()?.id ?? null;

  const setupGuideQuery = useQuery({
    queryKey: setupGuideQueryKey(workspaceId),
    queryFn: async () => getSetupGuide(workspaceId),
    enabled: enabled && workspaceId != null,
    placeholderData: keepPreviousWorkspaceData ? keepPreviousData : undefined,
    staleTime: QUERY_STALE_TIME,
  });

  // `workspaceId` is returned because writers need the id the guide was *read* under. Deriving it
  // again at the call site is how a write ends up on the wrong workspace: the obvious source,
  // `useWorkspaceIdFromCurrentUrl()`, is a different value on `/workspaces/last/*` and whenever
  // `useCurrentWorkspace` falls back to the default workspace.
  return {
    setupGuideQuery,
    workspaceId,
  };
};

/**
 * The cache key for a workspace's setup guide. Exported so nobody has to spell the tuple again:
 * an invalidation that builds it from a different workspace id than the query used simply misses,
 * silently, and the guide goes stale instead of erroring.
 */
export const setupGuideQueryKey = (workspaceId: number | null) => ['setupGuide', workspaceId] as const;

/**
 * Invalidate the current workspace's setup guide after writing to it.
 *
 * Takes the workspace from the same place `useSetupGuide` does, so the key always matches by
 * construction. Callers used to assemble it from `useWorkspaceIdFromCurrentUrl()`, which is a
 * digits-only parse of the path — on `/workspaces/last/*` that is `null` while the query is keyed
 * on the resolved id, so the invalidation would quietly hit nothing. FPS-4144.
 */
export const useInvalidateSetupGuide = () => {
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspace()?.id ?? null;

  // Stable so callers can list it in a dependency array. `react/exhaustive-deps` is a warning and
  // the lint script runs `--quiet`, so nothing would tell them if this identity churned.
  return useCallback(
    async () => queryClient.invalidateQueries({ queryKey: setupGuideQueryKey(workspaceId) }),
    [queryClient, workspaceId],
  );
};
